// apps/pwa-pos/src/lib/retry-policy.ts
// Sprint 99 POS.5 — política única de reintento compartida por las colas de
// ventas, caja y anulación.
//
// Un fallo transitorio (red caída, gateway, contención en la DB) se reintenta
// con backoff exponencial. Un fallo permanente (el servidor rechazó la
// operación por regla de negocio) no se reintenta solo: reintentarlo daría el
// mismo resultado y sólo genera ruido. Queda visible en la UI para que el
// cajero decida.

export type FalloTipo = 'transitorio' | 'permanente';

export const BACKOFF_BASE_MS = 15_000;
export const BACKOFF_MAX_MS = 5 * 60_000;

/** Error HTTP que conserva el status para poder clasificarlo en el catch. */
export class HttpFalloError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpFalloError';
    this.status = status;
  }
}

/**
 * Sprint 99 POS.5 — fallo reportado DENTRO de un 200 de `pos-upload`.
 *
 * La Edge Function atrapa toda excepción por venta y la empuja al array
 * `results` como `{ status: 'duplicate', error }`, devolviendo HTTP 200 igual.
 * El status HTTP no alcanza para saber si la venta se persistió: hay que leer
 * el body. Este error transporta la clasificación ya resuelta.
 */
export class UploadRechazadoError extends Error {
  readonly tipo: FalloTipo;

  constructor(message: string, tipo: FalloTipo, options?: ErrorOptions) {
    super(message, options);
    this.name = 'UploadRechazadoError';
    this.tipo = tipo;
  }
}

/**
 * Clasifica el string `error` que `pos-upload` adjunta a un resultado.
 *
 * El servidor mezcla en `status: 'duplicate'` tanto el reenvío idempotente como
 * la explosión, y no propaga el SQLSTATE: lo único que llega es el mensaje. Los
 * rechazos de negocio de las RPCs del POS vienen con un cuerpo JSON estructurado
 * (`{"code":"CREDITO_EXCEDIDO","message":"..."}`), así que esa es la única señal
 * en la que se puede confiar para declarar un fallo terminal.
 *
 * Ante la duda va transitorio a propósito: `pos-upload` es idempotente por
 * `ventas.id`, así que reintentar de más es recuperable — que la venta se
 * borre del equipo no lo es.
 */
const CODIGO_NEGOCIO_RE = /"code"\s*:\s*"([A-Z][A-Z0-9_]*)"/;

export function clasificarErrorUpload(mensaje: string): FalloTipo {
  return CODIGO_NEGOCIO_RE.test(mensaje) ? 'permanente' : 'transitorio';
}

/**
 * SQLSTATE que sí cambian de resultado al reintentar: caídas de conexión,
 * contención y agotamiento de recursos. Cualquier otro código emitido por la
 * base es determinístico y reintentarlo no cambia nada.
 */
const SQLSTATE_TRANSITORIOS = new Set([
  '08000', // connection_exception
  '08003', // connection_does_not_exist
  '08006', // connection_failure
  '40001', // serialization_failure
  '40P01', // deadlock_detected
  '53300', // too_many_connections
  '57014', // query_canceled
]);

export function clasificarHttp(status: number): FalloTipo {
  // 401/403 son transitorios a propósito: el access token puede haber expirado
  // mientras el equipo estaba offline y el próximo flush lo trae renovado.
  if (status === 401 || status === 403) return 'transitorio';
  if (status === 408 || status === 429) return 'transitorio';
  if (status >= 500) return 'transitorio';
  if (status >= 400) return 'permanente';
  return 'transitorio';
}

export function clasificarExcepcion(err: unknown): FalloTipo {
  if (err instanceof UploadRechazadoError) return err.tipo;
  if (err instanceof HttpFalloError) return clasificarHttp(err.status);
  // Todo lo demás llegó como excepción del fetch: red, DNS, timeout.
  return 'transitorio';
}

export function clasificarRpcError(
  error: { message?: string; code?: string } | null | undefined,
): FalloTipo {
  const code = error?.code;
  // Sin código no vino de Postgres: es red o gateway.
  if (!code) return 'transitorio';
  return SQLSTATE_TRANSITORIOS.has(code) ? 'transitorio' : 'permanente';
}

export function backoffMs(intentos: number): number {
  const n = Math.max(1, Math.floor(intentos));
  return Math.min(BACKOFF_BASE_MS * 2 ** (n - 1), BACKOFF_MAX_MS);
}

export function proximoIntentoAt(intentos: number, ahoraMs: number = Date.now()): string {
  return new Date(ahoraMs + backoffMs(intentos)).toISOString();
}

export function esReintentable(
  op: { estado: string; proximoIntentoAt?: string | null },
  ahoraMs: number = Date.now(),
): boolean {
  if (op.estado === 'pendiente') return true;
  if (op.estado !== 'error') return false;
  if (!op.proximoIntentoAt) return true;
  const t = Date.parse(op.proximoIntentoAt);
  if (Number.isNaN(t)) return true;
  return t <= ahoraMs;
}

/** Estado mínimo que comparten las tres colas para el reintento. */
export interface OpReintentable {
  estado: 'pendiente' | 'sincronizada' | 'error' | 'rechazada';
  errorMsg?: string;
  intentos: number;
  proximoIntentoAt: string | null;
}

/**
 * Aplica el resultado de un fallo a una op de cualquiera de las tres colas:
 * transitorio reintenta con backoff, permanente queda terminal.
 */
export function aplicarFalloAOp(
  op: OpReintentable,
  tipo: FalloTipo,
  err: unknown,
  ahoraMs: number = Date.now(),
): void {
  op.errorMsg = err instanceof Error ? err.message : String(err);
  if (tipo === 'permanente') {
    op.estado = 'rechazada';
    op.proximoIntentoAt = null;
  } else {
    op.intentos = (op.intentos ?? 0) + 1;
    op.estado = 'error';
    op.proximoIntentoAt = proximoIntentoAt(op.intentos, ahoraMs);
  }
}
